/**
 * Shared helpers for the Budget Compare drawer + the Variance Analysis PDF.
 *
 * Pure functions; no React, no hooks. The `formatMoney` / `formatDateShort`
 * formatters are passed in by the caller (drawer reads them from
 * `useVatMode()` + `utils/dateFormat`; the PDF layout passes the same via
 * props) so this module can be used identically from React rendering and
 * from server-side / test contexts.
 *
 * Single source of truth for:
 *   - Field-label translation (snake_case → human)
 *   - Field-type classification (money / date / weeks / allocation)
 *   - Canonical field render order (Schedule → Rates → Allowances)
 *   - Per-type value formatting
 *   - Effective-rate derivation when weekly_rate_override is null
 *   - Per-field impact maths (with sign convention: current − comparison;
 *     positive = cost increase, negative = savings)
 *   - Allowance-slot grouping (3 fields collapse into 1 row when 2+ changed)
 *
 * If a future field or field-handling tweak is added: update here, both the
 * drawer and the PDF inherit it.
 */

import { COMPUTER_ALLOWANCE_NAME } from '../constants/budget';

// snake_case → human-readable label.
export const COMPARE_FIELD_LABELS = {
  crew_grade_id: 'Grade',
  grade_name: 'Grade',
  start_date: 'Start date',
  end_date: 'End date',
  default_allocation: 'Default allocation',
  weekly_rate_override: 'Weekly rate',
  /* 🔑 COMPUTER RENTAL IS ONE ALLOWANCE, SO ITS TWO FIELDS ARE LABELLED LIKE THE
     OTHER THREE SLOTS' (2026-08-23). They read `Computer rental` / `Computer cap`
     while the named slots read `Allowance 1 amount` / `Allowance 1 cap` — a
     different naming family for the same pair of facts, which is one more way the
     diff presented computer as a category rather than as an allowance. The
     generic slots cannot name themselves (their name is user-supplied and may be
     blank), so they say "Allowance N"; this one CAN, and does. */
  computer_rental: 'Computer Rental amount',
  computer_rental_cap: 'Computer Rental cap',
  total_weeks: 'Total weeks',
  allowance_name: 'Allowance 1 name',
  allowance_amount: 'Allowance 1 amount',
  allowance_cap: 'Allowance 1 cap',
  allowance_name_2: 'Allowance 2 name',
  allowance_amount_2: 'Allowance 2 amount',
  allowance_cap_2: 'Allowance 2 cap',
  allowance_name_3: 'Allowance 3 name',
  allowance_amount_3: 'Allowance 3 amount',
  allowance_cap_3: 'Allowance 3 cap',
  /* R2 F1 (2026-10-28): the server names `budget_cost` ONLY when no input moved —
     the version's agreement terms or the worked days did — so it is the row that
     explains the member's money. Unranked, so it sorts last. */
  budget_cost: 'Total cost (terms / days)',
};
export const prettyField = (f) => COMPARE_FIELD_LABELS[f] || f;

// Type classification — keyed off the server's snake_case field names.
export const COMPARE_MONEY_FIELDS = new Set([
  'weekly_rate_override',
  'computer_rental', 'computer_rental_cap',
  'allowance_amount', 'allowance_cap',
  'allowance_amount_2', 'allowance_cap_2',
  'allowance_amount_3', 'allowance_cap_3',
  'budget_cost',
]);
export const COMPARE_DATE_FIELDS = new Set(['start_date', 'end_date']);
export const COMPARE_WEEK_FIELDS = new Set(['total_weeks']);
export const COMPARE_ALLOC_FIELDS = new Set(['default_allocation']);

// Canonical field order in the Modified diff: Schedule → Rates → Allowances.
//
// 🟢 ONLY THE GROUP COMMENT MOVED ON 2026-08-23 — THE ARRAY IS BYTE-FOR-BYTE
// THE ORDER IT HAS ALWAYS BEEN, AND SAYING SO IS THE POINT.
// 🔻 An earlier draft of this block claimed the two computer fields *"MOVED OUT
// OF Rates AND INTO Allowances"*. THEY DID NOT MOVE. Extracted and compared,
// the 18-entry list is identical before and after: `computer_rental` /
// `computer_rental_cap` already sat between `weekly_rate_override` and
// `allowance_name`, i.e. already first in the allowance block. All that was
// wrong was the `// Rates` boundary drawn ABOVE them, which filed them under
// the wrong heading for any reader — and it is the reader this whole change is
// about. The correction is recorded rather than quietly dropped because a
// comment claiming a move that never happened is exactly the kind of thing that
// gets "verified" by reading it.
// 🔑 FIRST WITHIN THE GROUP, matching the member form, where Computer Rental is
// allowance row #0 with its name locked. That was already true; the comment now
// says it.
// 🪤 THE ORDER IS NOT COSMETIC, WHICH IS WHY ITS CORRECTNESS IS WORTH PINNING
// EVEN THOUGH THIS CHANGE DID NOT PRODUCE IT — `compareFieldRank` orders the
// GROUPED slot rows too (`buildModifiedRows` ranks each slot by its
// `amountField`), so these two entries place the computer SLOT row as well as
// the two ungrouped field rows. A future edit that "tidies" them back up beside
// the weekly rate would move a slot row a hundred lines away from the edit.
// 🟢 No arithmetic and no grouping threshold changed anywhere in this file:
// `buildModifiedRows` already treated computer as a `PAIR_SLOT` alongside the
// three named allowances — same `describeSide`, same `slotContribution`, same
// impact maths. What DID change is two LABELS and the slot's own label.
export const COMPARE_FIELD_ORDER = [
  // Schedule
  'start_date', 'end_date', 'total_weeks', 'default_allocation',
  // Rates
  'crew_grade_id', 'grade_name', 'weekly_rate_override',
  // Allowances — the built-in computer slot first, then the three named ones
  'computer_rental', 'computer_rental_cap',
  'allowance_name', 'allowance_amount', 'allowance_cap',
  'allowance_name_2', 'allowance_amount_2', 'allowance_cap_2',
  'allowance_name_3', 'allowance_amount_3', 'allowance_cap_3',
];
export const compareFieldRank = (f) => {
  const i = COMPARE_FIELD_ORDER.indexOf(f);
  return i === -1 ? 999 : i;
};

// Allowance slot grouping descriptors. When 2+ of a slot's 3 fields changed,
// render the slot as ONE combined row instead of three separate rows.
export const COMPARE_ALLOWANCE_SLOTS = [
  { key: 'allowance_1', label: 'Allowance 1', nameField: 'allowance_name',   amountField: 'allowance_amount',   capField: 'allowance_cap'   },
  { key: 'allowance_2', label: 'Allowance 2', nameField: 'allowance_name_2', amountField: 'allowance_amount_2', capField: 'allowance_cap_2' },
  { key: 'allowance_3', label: 'Allowance 3', nameField: 'allowance_name_3', amountField: 'allowance_amount_3', capField: 'allowance_cap_3' },
];

// The built-in COMPUTER allowance slot — same {amount, cap} pair shape as any
// other allowance, but with no separate name field, because this one's name is
// fixed. Groups on ANY single field change (vs a named allowance's 2-of-3
// threshold) because the two fields describe the same conceptual line: there is
// no scenario where they should be shown separately.
//
// 🔑 THE LABEL IS THE ALLOWANCE'S NAME, and it is the shared constant. The three
// named slots fall back to `Allowance N` only when the user has typed no name;
// this one always has one. Do not change it to "Computer Allowance" — that is
// the label for an AMOUNT CONTROL (`COMPUTER_ALLOWANCE_LABEL`), and here the
// slot row is identified the way a named slot is: by its name.
export const COMPARE_COMPUTER_SLOT = {
  key: 'computer',
  label: COMPUTER_ALLOWANCE_NAME,
  amountField: 'computer_rental',
  capField: 'computer_rental_cap',
};

/**
 * THE value of one compare field on one side — for the 11 allowance-slot
 * columns, the RESOLVED slot the server publishes on each side
 * (`allowances.<slot>.{amount,cap,name}`), never the raw column (allow-ui
 * review F1, 2026-10-25). Grade allowances made a FOLLOWING member's raw slot
 * NULL while it costs the grade's figure, so reading the raw column rendered
 * "Box: — → £40.00/wk, impact −£80.00" for a £0 change, and −£80 for a +£40
 * one. Every slot read in this file goes through here; the server buckets on
 * the same resolved values (`helpers/crewCost.js#resolvedAllowanceColumn`).
 * 🪤 FALLBACK: a side with no `allowances` object is a payload from before
 * grade allowances existed, when the raw column WAS the resolved value — so
 * the raw read there is correct, not a second opinion. Any other field is
 * read straight off the side.
 */
const SLOT_OF_FIELD = Object.fromEntries(
  [COMPARE_COMPUTER_SLOT, ...COMPARE_ALLOWANCE_SLOTS].flatMap((s) => [
    [s.amountField, [s.key, 'amount']],
    [s.capField, [s.key, 'cap']],
    ...(s.nameField ? [[s.nameField, [s.key, 'name']]] : []),
  ]),
);
export const compareSideValue = (side, field) => {
  const at = SLOT_OF_FIELD[field];
  if (!at || !side?.allowances) return side?.[field];
  const v = side.allowances[at[0]]?.[at[1]];
  return v === undefined ? null : v;
};

// Unified "{amount,cap} pair" slot list — allowances (3 fields incl. name,
// group on 2+ of 3 changed) + computer rental (2 fields, no name, group on any
// single change because the two fields describe one conceptual line).
const PAIR_SLOTS = [
  { ...COMPARE_COMPUTER_SLOT, nameField: null, groupThreshold: 1 },
  ...COMPARE_ALLOWANCE_SLOTS.map((s) => ({ ...s, groupThreshold: 2 })),
];
const PAIR_SLOT_OF_FIELD = Object.fromEntries(
  PAIR_SLOTS.flatMap((s) => [[s.amountField, s], [s.capField, s]]),
);
/** One side's capped contribution for a slot: min(resolved amount × weeks, resolved cap). */
const slotContributionFor = (slot, side) => {
  const amt = Number(compareSideValue(side, slot.amountField)) || 0;
  const weeks = Number(side?.total_weeks) || 0;
  const cap = compareSideValue(side, slot.capField);
  const raw = amt * weeks;
  const capNum = cap != null ? Number(cap) : null;
  return capNum != null ? Math.min(raw, capNum) : raw;
};

/**
 * Type-aware value formatter for Modified diff cells.
 * @param {string} field  snake_case field name
 * @param {*} value       raw value from compare response (may be null/undefined)
 * @param {{ formatMoney: (n)=>string, formatDateShort: (s)=>string }} fmt
 * @returns {string}      formatted display, or "—" for nullish/empty
 */
export const fmtCompareValue = (field, value, fmt) => {
  if (value == null || value === '') return '—';
  if (COMPARE_DATE_FIELDS.has(field)) return fmt.formatDateShort(value);
  if (COMPARE_MONEY_FIELDS.has(field)) return fmt.formatMoney(value);
  if (COMPARE_WEEK_FIELDS.has(field)) {
    const n = Number(value);
    return `${n} wk${n === 1 ? '' : 's'}`;
  }
  if (COMPARE_ALLOC_FIELDS.has(field)) return `${(Number(value) * 100).toFixed(0)}%`;
  return String(value);
};

/**
 * Effective weekly rate per side. Used by the `total_weeks` impact row, which
 * feeds the Compare drawer and the Variance Analysis PDF.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 🔴 THIS READ THE SERVER'S FIGURE FROM 2026-08-21 (S2-C). IT USED TO DIVIDE.
 * ─────────────────────────────────────────────────────────────────────────
 * It was `budget_cost / total_weeks`, and that is THE identity the 6th/7th-day
 * day model breaks. Two independent reasons, either one fatal:
 *
 *  1. **`budget_cost` is not labour.** It is labour PLUS capped computer rental
 *     PLUS all three allowances (`routes/budget.js`'s `decorate()` sums five
 *     terms into it). Dividing it by weeks never gave a weekly RATE — it gave a
 *     blended per-week cost, and the error was invisible only because it was
 *     applied to a weeks DELTA, where rental and allowances happen to scale the
 *     same way.
 *  2. **The two operands are now on DIFFERENT BASES.** `budget_cost`'s labour
 *     term is day-derived (`worked_days / 5`); `total_weeks` is week-derived
 *     from the allocation fractions. They agree only while `worked_days / 5`
 *     equals `total_weeks`, which is exactly what S2-A restores for members
 *     whose day rows are all presumed — and exactly what a HAND-RECORDED member
 *     is free to break, because their days are facts and the system may not
 *     rewrite them to keep a ratio tidy.
 *
 * 🔑 So the rate is now PUBLISHED, not reconstructed: `effective_weekly_rate`
 * comes from `helpers/crewCost.js#resolveEffectiveRate`, the same resolver
 * every server cost site uses, so the drawer cannot hold a different opinion
 * about one member's rate from the budget it is comparing.
 *
 * 🪤 THE FALLBACK IS DELIBERATELY THE OLD DIVISION AND MUST NOT BE A THROW OR A
 * ZERO. A cached or older payload has no `effective_weekly_rate`; zero would
 * silently report every schedule change as costing nothing (the "budget looks
 * healthier than it is" direction), and a throw would take out a drawer over a
 * missing optional field. The division is the pre-S2-C answer, so the fallback
 * cannot move any figure that is not already moving.
 * 🪤 `?? ` and `Number.isFinite`, never `||` — a legitimately ZERO rate (an
 * unpaid member, a grade with no rate) is falsy, and `||` would push it onto
 * the fallback and invent money for someone who costs nothing.
 */
export const effectiveRateFor = (side) => {
  const published = Number(side?.effective_weekly_rate);
  if (Number.isFinite(published)) return published;
  const weeks = Number(side?.total_weeks) || 0;
  const cost = Number(side?.budget_cost) || 0;
  return weeks > 0 ? cost / weeks : 0;
};

/**
 * Per-field cost impact. Returns { text, tone }.
 *   tone: 'red' (cost increase), 'emerald' (savings), 'mute' (no impact /
 *         non-financial / schedule INPUT). A lone allowance cap row carries
 *         its slot's real capped delta since 2026-10-25 (F1).
 *
 * SIGN CONVENTION: positive when current (a) costs more than comparison (b).
 *
 * The Mason Kowalski scenario (2026-05-31) baked in two key rules here:
 *   - weekly_rate_override null means "use grade rate", not £0. Compute impact
 *     from effective rates, not raw override values.
 *   - Schedule INPUT fields (start_date, end_date, default_allocation) drive
 *     total_weeks — their impact is captured ONCE on the total_weeks row to
 *     prevent the user triple-counting.
 */
export const fmtCompareImpact = (field, row, fmt) => {
  const a = row.a || {};
  const b = row.b || {};
  const mute = { text: '—', tone: 'mute' };

  const NON_FINANCIAL = new Set([
    'crew_grade_id', 'grade_name',
    'allowance_name', 'allowance_name_2', 'allowance_name_3',
  ]);
  if (NON_FINANCIAL.has(field)) return mute;

  // Schedule INPUT fields → muted; impact lives once on total_weeks.
  const SCHEDULE_INPUTS = new Set(['start_date', 'end_date', 'default_allocation']);
  if (SCHEDULE_INPUTS.has(field)) return mute;

  let deltaCost = 0;
  const pairSlot = PAIR_SLOT_OF_FIELD[field];

  if (pairSlot) {
    // An allowance AMOUNT or CAP alone (F1, 2026-10-25): the slot's own capped
    // contribution on each side — the same `min(amount × weeks, cap)` the
    // grouped slot row and the server use. 🔻 A lone cap row was MUTED ("only
    // binds when uncapped cost > cap") and a lone amount row ignored the cap;
    // with the resolved cap on each side the conditional is computable, and a
    // muted dash beside a real server delta is the misstatement F1 named.
    deltaCost = slotContributionFor(pairSlot, a) - slotContributionFor(pairSlot, b);
  } else if (field === 'weekly_rate_override') {
    // null means "use grade rate"; compute from effective rates.
    const effRate = (side) =>
      Number(side?.weekly_rate_override) || Number(side?.grade_weekly_rate) || 0;
    const aRate = effRate(a);
    const bRate = effRate(b);
    const aWeeks = Number(a.total_weeks) || 0;
    deltaCost = (aRate - bRate) * aWeeks;
  } else if (field === 'budget_cost') {
    // The residual (server names it only when no input moved) — the whole member delta.
    deltaCost = (Number(a.budget_cost) || 0) - (Number(b.budget_cost) || 0);
  } else if (field === 'total_weeks') {
    const aWeeks = Number(a.total_weeks) || 0;
    const bWeeks = Number(b.total_weeks) || 0;
    deltaCost = (aWeeks - bWeeks) * effectiveRateFor(a);
  } else {
    return mute;
  }

  if (Math.abs(deltaCost) < 0.005) return { text: fmt.formatMoney(0), tone: 'mute' };
  const tone = deltaCost > 0 ? 'red' : 'emerald';
  const sign = deltaCost > 0 ? '+' : '−';
  return { text: `${sign}${fmt.formatMoney(Math.abs(deltaCost))}`, tone };
};

/**
 * Build the ordered + slot-grouped row list for a modified member.
 * Each returned row is one of:
 *   { kind: 'field', field, aDisplay, bDisplay, impact, rank }
 *   { kind: 'slot',  slotKey, label, aDisplay, bDisplay, impact, rank }
 *
 * Sorted by canonical field rank (schedule → rates → allowances).
 *
 * `effRateDisplay` is special-cased for weekly_rate_override: when null on
 * one side, show the grade rate with a "(grade)" annotation rather than "—".
 */
export const buildModifiedRows = (row, fmt) => {
  const changed = new Set(row.changed_fields || []);
  const a = row.a || {};
  const b = row.b || {};

  const groupedSlotKeys = new Set();
  const slotRows = [];
  for (const slot of PAIR_SLOTS) {
    const slotFields = [slot.nameField, slot.amountField, slot.capField].filter(Boolean);
    const changedCount = slotFields.filter((f) => changed.has(f)).length;
    if (changedCount >= slot.groupThreshold) {
      groupedSlotKeys.add(slot.key);

      // Value cell — amount + cap only. The allowance's NAME has moved to
      // the row label (left of the table), so we no longer duplicate it
      // here. Computer rental has no nameField; same logic applies.
      const describeSide = (side) => {
        const amt = compareSideValue(side, slot.amountField);
        const cap = compareSideValue(side, slot.capField);
        if ((amt == null || Number(amt) === 0) && cap == null) return '—';
        const parts = [];
        if (amt != null && Number(amt) !== 0) parts.push(`${fmt.formatMoney(amt)}/wk`);
        if (cap != null) parts.push(`cap ${fmt.formatMoney(cap)}`);
        return parts.length ? parts.join(' · ') : '—';
      };

      // Sign convention: current (a) − comparison (b).
      const deltaCost = slotContributionFor(slot, a) - slotContributionFor(slot, b);
      let impact;
      if (Math.abs(deltaCost) < 0.005) {
        impact = { text: fmt.formatMoney(0), tone: 'mute' };
      } else {
        const tone = deltaCost > 0 ? 'red' : 'emerald';
        const sign = deltaCost > 0 ? '+' : '−';
        impact = { text: `${sign}${fmt.formatMoney(Math.abs(deltaCost))}`, tone };
      }

      // Row label — use the allowance's actual name when available so the
      // user sees "Box" not the generic "Allowance 1". Falls back through:
      //   1. Current side's name (e.g. just renamed → most relevant)
      //   2. Comparison side's name (e.g. allowance removed in current)
      //   3. Slot label (computer rental, or both names empty)
      // If both sides have names but they differ, annotate the rename in
      // parentheses so the user can see what it used to be called.
      const aName = slot.nameField ? compareSideValue(a, slot.nameField) : null;
      const bName = slot.nameField ? compareSideValue(b, slot.nameField) : null;
      let rowLabel;
      if (aName && bName && String(aName) !== String(bName)) {
        rowLabel = `${String(aName)} (was ${String(bName)})`;
      } else if (aName) {
        rowLabel = String(aName);
      } else if (bName) {
        rowLabel = String(bName);
      } else {
        rowLabel = slot.label;
      }

      slotRows.push({
        kind: 'slot',
        slotKey: slot.key,
        label: rowLabel,
        aDisplay: describeSide(a),
        bDisplay: describeSide(b),
        impact,
        rank: compareFieldRank(slot.amountField),
      });
    }
  }

  const absorbedFields = new Set();
  for (const slot of PAIR_SLOTS) {
    if (groupedSlotKeys.has(slot.key)) {
      if (slot.nameField) absorbedFields.add(slot.nameField);
      absorbedFields.add(slot.amountField);
      absorbedFields.add(slot.capField);
    }
  }

  const effRateDisplay = (side) => {
    const override = Number(side?.weekly_rate_override);
    if (Number.isFinite(override) && override > 0) return fmt.formatMoney(override);
    const grade = Number(side?.grade_weekly_rate);
    if (Number.isFinite(grade) && grade > 0) return `${fmt.formatMoney(grade)} (grade)`;
    return '—';
  };

  const fieldRows = [];
  for (const f of changed) {
    if (absorbedFields.has(f)) continue;
    const isOverrideField = f === 'weekly_rate_override';
    fieldRows.push({
      kind: 'field',
      field: f,
      aDisplay: isOverrideField ? effRateDisplay(a) : fmtCompareValue(f, compareSideValue(a, f), fmt),
      bDisplay: isOverrideField ? effRateDisplay(b) : fmtCompareValue(f, compareSideValue(b, f), fmt),
      impact: fmtCompareImpact(f, row, fmt),
      rank: compareFieldRank(f),
    });
  }

  return [...slotRows, ...fieldRows].sort((x, y) => x.rank - y.rank);
};
